import { isSupportedType } from 'lib/ImageSupport';
import cx from 'classnames';
import * as React from 'react';

import './styles/Image.scss';

/**
 * ## Image with an empty `alt` attribute so that presentational images are ignored by screenreaders by default.
 *
 *
 * ```example
 * <Image src="https://www.remind.com/assets/images/app-screenshot.jpg" />
 * ```
 *
 * ```example
 * <Image
 *   alt="Image screenreader text"
 *   circle
 *   src="https://www.remind.com/assets/images/app-download-icon-72.png"
 * />
 * ```
 *
 *
 * # TODO
 * * `draggable` in `propTypes`?
 * * `draggable` default to native `auto` unless explicitly set?
 */

type Props = {
  /** Circular-shaped if equal `width` & `height` */
  circle?: boolean;
  /** Emulate `background-size: cover` via `object-fit` */
  cover?: boolean;
  /** Disallow dragging image */
  disableUserInteraction?: boolean;
  /** Full-width */
  full?: boolean;
  className?: string;
  roundedCorners?: boolean;
} & React.AllHTMLAttributes<HTMLImageElement>;

export default class Image extends React.Component<Props> {
  static defaultProps = {
    alt: '',
    onLoad: () => {},
    onError: () => {},
  };

  static isSupportedType = isSupportedType;

  private img = React.createRef<HTMLImageElement>();

  getImgNode = (): HTMLImageElement | null => {
    return this.img.current;
  };

  isLandscape = (): boolean => {
    if (this.img.current) {
      return this.img.current.naturalWidth > this.img.current.naturalHeight;
    }
    return false;
  };

  render() {
    const {
      alt,
      title,
      circle,
      className,
      cover,
      disableUserInteraction,
      full,
      height,
      onLoad,
      onError,
      src,
      srcSet,
      style,
      width,
      roundedCorners,
    } = this.props;

    return (
      <img
        ref={this.img}
        alt={alt}
        title={title}
        draggable={!disableUserInteraction}
        height={height}
        src={src}
        srcSet={srcSet}
        style={style}
        width={width}
        // Setting the key on images is a fix for Safari not rendering SVGs
        // correctly when you change their src. This way is the src changes, it
        // makes a new img element and Safari renders it correctly.
        key={src}
        className={cx(
          'Image',
          {
            'Image--circle': circle,
            'Image--cover': cover,
            'Image--full': full,
            'Image--interactionDisabled': disableUserInteraction,
            'Image--roundedCorners': roundedCorners,
          },
          className
        )}
        onLoad={onLoad}
        onError={onError}
      />
    );
  }
}
